import { Link } from "react-router-dom";
import {
  ArrowRight,
  Detective,
  Target,
  CheckCircle,
  HandHeart,
  Lightning,
  Lock,
  Star,
  Compass,
  Infinity as InfinityIcon,
} from "@phosphor-icons/react";

export default function Companies() {
  return (
    <div data-testid="companies-page">
      {/* HERO */}
      <section className="relative bg-navy-deep text-white overflow-hidden noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/15">
                <span className="eyebrow text-purple-accent">Entreprises</span>
              </div>
              <h1 className="mt-8 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
                Recrutez les talents <br />
                qui feront <span className="font-serif-italic text-purple-accent">avancer</span> <br />
                votre organisation
              </h1>
              <div className="accent-line mt-8" />
              <p className="mt-8 text-white/75 max-w-xl leading-relaxed text-[16px]">
                MP Advisory accompagne les entreprises dans leurs recrutements
                stratégiques Sales et IT. Nous identifions les profils qui
                correspondent à vos enjeux, à votre culture et à vos ambitions
                de développement.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/formulaire"
                  data-testid="companies-cta-need"
                  className="group inline-flex items-center gap-3 rounded-full bg-purple-accent hover:bg-purple-accent-hover text-white px-8 py-4 text-[15px] font-medium transition-all hover:-translate-y-0.5 shadow-purple-glow"
                >
                  Soumettre un besoin
                  <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="companies-cta-contact"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:border-white hover:bg-white/5 text-white px-8 py-4 text-[15px] font-medium transition-all"
                >
                  Prendre contact
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-white/10">
                <img
                  src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                  alt="Équipe entreprise partenaire"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/50 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE BLOCKS */}
      <section className="bg-body-bg py-24 lg:py-32" data-testid="companies-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="eyebrow text-purple-accent">Notre méthodologie</p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-navy-deep leading-[1.1]">
              Une démarche <span className="font-serif-italic text-purple-accent">rigoureuse</span> et sur mesure
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Detective, num: "01", title: "Compréhension de votre besoin", desc: "Analyse du poste, de l'environnement, des enjeux et des critères de réussite. Nous prenons le temps de bien définir ensemble la mission." },
              { icon: Target, num: "02", title: "Sourcing ciblé", desc: "Identification de profils qualifiés, en recherche active ou ouverts à de nouvelles opportunités, qui correspondent réellement à vos attentes." },
              { icon: CheckCircle, num: "03", title: "Évaluation rigoureuse", desc: "Préqualification, analyse du parcours, des motivations, des compétences et de la compatibilité culturelle avec votre organisation." },
              { icon: HandHeart, num: "04", title: "Suivi jusqu'à l'intégration", desc: "Accompagnement jusqu'à la prise de poste pour sécuriser le processus et garantir la réussite du recrutement." },
            ].map((f, i) => (
              <article
                key={i}
                data-testid={`companies-feature-${i}`}
                className="bg-white rounded-2xl p-8 lg:p-10 border border-gray-100 shadow-subtle lift-on-hover flex gap-6"
              >
                <div className="shrink-0">
                  <div className="w-14 h-14 rounded-2xl bg-purple-soft flex items-center justify-center">
                    <f.icon size={28} weight="duotone" className="text-purple-accent" />
                  </div>
                  <div className="step-number text-3xl text-purple-accent/40 mt-4 text-center">{f.num}</div>
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl text-navy-deep">{f.title}</h3>
                  <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{f.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="bg-navy-deep text-white py-24 lg:py-32 relative overflow-hidden noise-overlay" data-testid="companies-commitments">
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-purple-accent/10 rounded-full blur-[140px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="eyebrow text-purple-accent">Nos engagements</p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-[1.1]">
              Cinq promesses, <span className="font-serif-italic text-purple-accent">une seule exigence</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: Lightning, title: "Réactivité" },
              { icon: Lock, title: "Confidentialité" },
              { icon: Star, title: "Qualité des profils" },
              { icon: Compass, title: "Suivi personnalisé" },
              { icon: InfinityIcon, title: "Vision long terme" },
            ].map((c, i) => (
              <div
                key={i}
                data-testid={`commitment-${i}`}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 backdrop-blur-sm hover:bg-white/[0.08] transition-colors text-center"
              >
                <div className="mx-auto w-14 h-14 rounded-xl bg-purple-accent/15 flex items-center justify-center">
                  <c.icon size={28} weight="duotone" className="text-purple-accent" />
                </div>
                <h3 className="mt-5 font-serif text-lg">{c.title}</h3>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/formulaire"
              data-testid="commitments-cta"
              className="inline-flex items-center gap-2 rounded-full bg-purple-accent hover:bg-purple-accent-hover text-white px-8 py-4 text-sm font-medium transition-all hover:-translate-y-0.5 shadow-purple-glow"
            >
              Lancer une mission de recrutement
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
